import {DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {TreatmentRequestExtended, TreatmentRequestTreatmentValidationSchema} from '../../types'
import {useTranslation} from '@/translations/i18n'
import {useTreatmentRequestEditModeView} from '@/features/treatment-requests/hooks/useTreatmentRequestEditModeView'
import {TreatmentRequestEditButtons} from '@/features/treatment-requests/components/treatment-request-edit-buttons/TreatmentRequestEditButtons'
import {getDirtyFieldsFromFormValues, ObjectKeys} from '@/utilities/helpers'
import {FormProvider, useForm, useFormContext} from 'react-hook-form'
import {TreatmentRequestTreatmentForm} from '@/features/treatment-requests/components/treatment-request-treatment-form/TreatmentRequestTreatmentForm'
import {TreatmentRequestTreatmentDetails} from '@/features/treatment-requests/components/treatment-request-treatment-details/TreatmentRequestTreatmentDetails'
import {useOutletContext} from 'react-router-dom'
import {useTreatmentRequestFormSubmission} from '@/features/treatment-requests/hooks/useTreatmentRequestFormSubmission'
import {remapTreatmentRequestToDefaultValues, remapTreatmentRequestToPayload} from '@/features/treatment-requests/utils'
import {useUpdateTreatmentRequest} from '@/features/treatment-requests/services/useUpdateTreatmentRequest'
import {useTreatmentRequestDetailsStore} from '@/features/treatment-requests/store/treatmentRequestDetailsStore'
import {zodResolver} from '@hookform/resolvers/zod'
import {usePermissions} from '@/features/auth/store/store'

export const TreatmentRequestTreatmentSection = () => {
    const {treatmentRequest} = useOutletContext<{treatmentRequest: TreatmentRequestExtended}>()
    const defaultValues = remapTreatmentRequestToDefaultValues(treatmentRequest)
    const form = useForm<TreatmentRequestTreatmentValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(TreatmentRequestTreatmentValidationSchema),
        defaultValues: {
            patient: defaultValues.patient,
            project: defaultValues.project,
            type: defaultValues.type,
            sector: defaultValues.sector,
            authorization: defaultValues.authorization,
            authorizationToRequest: defaultValues.authorizationToRequest,
            authorizationDate: defaultValues.authorizationDate,
            scores: defaultValues.scores,
            diseases: defaultValues.diseases
        }
    })

    return (
        <FormProvider {...form}>
            <TreatmentRequestTreatmentSectionContent treatmentRequest={treatmentRequest} />
        </FormProvider>
    )
}

const TreatmentRequestTreatmentSectionContent = ({treatmentRequest}: {treatmentRequest: TreatmentRequestExtended}) => {
    const {t} = useTranslation()
    const form = useFormContext<TreatmentRequestTreatmentValidationSchema>()
    const {onSuccessSubmit, onErrorSubmit, scrollToTop} = useTreatmentRequestFormSubmission({
        formSchema: TreatmentRequestTreatmentValidationSchema
    })
    const permissions = usePermissions()
    const setIsErrorBannerVisible = useTreatmentRequestDetailsStore(state => state.setIsErrorBannerVisible)
    const {isEditModeEnabled, isDisabledView} = useTreatmentRequestEditModeView('treatment')

    // mutation
    const updateTreatmentRequest = useUpdateTreatmentRequest({
        urlParams: {treatmentRequestId: treatmentRequest.id},
        params: {responseType: 'extended'},
        options: {
            onSuccess: onSuccessSubmit,
            onError: onErrorSubmit
        }
    })

    // actions
    const handleSubmit = form.handleSubmit(
        formValues => {
            const dirtyFormValues = getDirtyFieldsFromFormValues(form.formState.dirtyFields, formValues, [])
            const parsedValues = remapTreatmentRequestToPayload(dirtyFormValues)
            return updateTreatmentRequest.mutate(parsedValues)
        },
        errors => {
            setIsErrorBannerVisible(true)
            console.error(errors)
            scrollToTop()
        }
    )
    return (
        <DetailsFieldsSection
            title={t('commons:treatment')}
            isDisabled={isDisabledView}
            headingEndSlot={
                permissions?.requests?.write && (
                    <TreatmentRequestEditButtons
                        groupName="treatment"
                        isConfirmDisabled={!ObjectKeys(form.formState.dirtyFields).length}
                        onCancel={form.reset}
                        onConfirm={handleSubmit}
                        isDisabled={treatmentRequest.status == 'accepted' || treatmentRequest.status == 'rejected'}
                    />
                )
            }
        >
            {isEditModeEnabled ? (
                <TreatmentRequestTreatmentForm
                    patientId={treatmentRequest.patientId}
                    treatmentRequestStatus={treatmentRequest.status}
                />
            ) : (
                <TreatmentRequestTreatmentDetails treatmentRequest={treatmentRequest} />
            )}
        </DetailsFieldsSection>
    )
}
